import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useUpdateProduct, type Product } from "@/hooks/useProducts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ChevronLeft, Save } from "lucide-react";
import { toast } from "sonner";
import AdminVariationsManager from "@/components/admin/AdminVariationsManager";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const AdminEditProduct = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const updateProduct = useUpdateProduct();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [showTypeWarning, setShowTypeWarning] = useState(false);

  useEffect(() => {
    if (!id) return;
    supabase
      .from("products")
      .select("*")
      .eq("id", Number(id))
      .single()
      .then(({ data, error }) => {
        if (error) {
          toast.error("Product not found");
          navigate("/admin");
        } else {
          setProduct(data);
        }
        setLoading(false);
      });
  }, [id]);

  const handleSave = async () => {
    if (!product) return;
    try {
      await updateProduct.mutateAsync({
        id: product.id,
        name: product.name,
        short_description: product.short_description,
        description: product.description,
        regular_price: product.regular_price,
        sale_price: product.sale_price,
        categories: product.categories,
        images: product.images,
        in_stock: product.in_stock,
        stock: product.stock,
        published: product.published,
        sku: product.sku,
        attribute_1_name: product.attribute_1_name,
        attribute_1_values: product.attribute_1_values,
        type: product.type,
      });
      toast.success("Product updated!");
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  if (loading) {
    return <div className="text-muted-foreground py-20 text-center">Loading…</div>;
  }

  if (!product) return null;

  const update = (field: keyof Product, value: any) => {
    setProduct({ ...product, [field]: value });
  };

  return (
    <div className="max-w-3xl">
      <button
        onClick={() => navigate("/admin/products")}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-6 font-body"
      >
        <ChevronLeft className="w-4 h-4" /> Back to Products
      </button>

      <h1 className="font-display text-2xl font-bold mb-6">Edit Product</h1>

      <div className="space-y-6 bg-card rounded-lg border border-border p-6">
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <Label>Name</Label>
            <Input value={product.name} onChange={(e) => update("name", e.target.value)} />
          </div>
          <div>
            <Label>SKU</Label>
            <Input value={product.sku || ""} onChange={(e) => update("sku", e.target.value)} />
          </div>
          <div>
            <Label>Product Type</Label>
            <select
              value={product.type}
              onChange={(e) => {
                const newType = e.target.value as "simple" | "variable";
                if (product.type === "variable" && newType === "simple") {
                  setShowTypeWarning(true);
                } else {
                  update("type", newType);
                }
              }}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="simple">Simple</option>
              <option value="variable">Variable</option>
            </select>
          </div>
        </div>

        <div>
          <Label>Short Description</Label>
          <Textarea
            value={product.short_description || ""}
            onChange={(e) => update("short_description", e.target.value)}
            rows={3}
          />
        </div>

        <div>
          <Label>Full Description</Label>
          <Textarea
            value={product.description || ""}
            onChange={(e) => update("description", e.target.value)}
            rows={5}
          />
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <Label>Regular Price (£)</Label>
            <Input
              type="number"
              step="0.01"
              value={product.regular_price ?? ""}
              onChange={(e) => update("regular_price", e.target.value ? Number(e.target.value) : null)}
            />
          </div>
          <div>
            <Label>Sale Price (£)</Label>
            <Input
              type="number"
              step="0.01"
              value={product.sale_price ?? ""}
              onChange={(e) => update("sale_price", e.target.value ? Number(e.target.value) : null)}
            />
          </div>
          <div>
            <Label>Stock</Label>
            <Input
              type="number"
              value={product.stock ?? ""}
              onChange={(e) => update("stock", e.target.value ? Number(e.target.value) : null)}
            />
          </div>
        </div>

        <div>
          <Label>Categories</Label>
          <Input value={product.categories || ""} onChange={(e) => update("categories", e.target.value)} />
        </div>

        <div>
          <Label>Images (comma-separated URLs)</Label>
          <Textarea
            value={product.images || ""}
            onChange={(e) => update("images", e.target.value)}
            rows={2}
          />
        </div>

        {product.type === "variable" && (
          <>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Attribute Name</Label>
                <Input value={product.attribute_1_name || ""} onChange={(e) => update("attribute_1_name", e.target.value)} />
              </div>
              <div>
                <Label>Attribute Values (comma-separated)</Label>
                <Input value={product.attribute_1_values || ""} onChange={(e) => update("attribute_1_values", e.target.value)} />
              </div>
            </div>

            <div className="border-t border-border pt-6">
              <AdminVariationsManager product={product} />
            </div>
          </>
        )}

        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 font-body text-sm">
            <input
              type="checkbox"
              checked={product.in_stock}
              onChange={(e) => update("in_stock", e.target.checked)}
              className="rounded border-input"
            />
            In Stock
          </label>
          <label className="flex items-center gap-2 font-body text-sm">
            <input
              type="checkbox"
              checked={product.published}
              onChange={(e) => update("published", e.target.checked)}
              className="rounded border-input"
            />
            Published
          </label>
        </div>

        <Button onClick={handleSave} disabled={updateProduct.isPending} className="gap-2">
          <Save className="w-4 h-4" />
          {updateProduct.isPending ? "Saving…" : "Save Changes"}
        </Button>
      </div>

      <AlertDialog open={showTypeWarning} onOpenChange={setShowTypeWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Convert to Simple Product?</AlertDialogTitle>
            <AlertDialogDescription>
              This product has variations that will become orphaned if you convert it to a simple product. The variation records will remain in the database but will no longer be linked to this product.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => update("type", "simple")}>
              Convert to Simple
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminEditProduct;
